import React, { useState, useMemo } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Calendar as CalendarIcon, ArrowLeftRight, Users, ChevronUp, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import FerryCard from "./FerryCard";

/**
 * @typedef {Object} FerrySearchViewProps
 * @property {Array<any>} ferries
 * @property {function} onSelectFerry
 * @property {boolean} [isLoading]
 * @property {any} [prefillDate]
 * @property {number} [prefillPassengers]
 */

/**
 * @param {FerrySearchViewProps} props
 */
export default function FerrySearchView(props) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const PopoverContentComp = /** @type {React.ComponentType<any>} */ (
    PopoverContent
  );

  //   const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const { ferries, onSelectFerry, isLoading, prefillDate, prefillPassengers } =
    props;
  const [originPort, setOriginPort] = useState("");
  const [destinationPort, setDestinationPort] = useState("");
  const [selectedDate, setSelectedDate] = useState(prefillDate || null);
  const [returnDate, setReturnDate] = useState(undefined);
  const [tripType, setTripType] = useState("all");
  const [passengerCount, setPassengerCount] = useState(prefillPassengers || 1);

  // Extract unique ports from ferry data
  const ports = useMemo(() => {
    const allPorts = new Set();
    ferries.forEach((ferry) => {
      allPorts.add(ferry.origin_port);
      allPorts.add(ferry.destination_port);
    });
    return Array.from(allPorts).sort();
  }, [ferries]);

  const filteredFerries = useMemo(() => {
    return ferries.filter((ferry) => {
      const matchesOrigin = !originPort || ferry.origin_port === originPort;
      const matchesDestination =
        !destinationPort || ferry.destination_port === destinationPort;

      if (tripType === "all") {
        return matchesOrigin && matchesDestination;
      } else if (tripType === "round-trip") {
        return (
          matchesOrigin &&
          matchesDestination &&
          ferry.supports_round_trip !== false
        );
      } else {
        return (
          matchesOrigin &&
          matchesDestination &&
          ferry.one_way_available !== false
        );
      }
    });
  }, [ferries, originPort, destinationPort, tripType]);

  const swapPorts = () => {
    const temp = originPort;
    setOriginPort(destinationPort);
    setDestinationPort(temp);
  };

  const showAllFerries = () => {
    setOriginPort("");
    setDestinationPort("");
  };

  return (
    <div className="w-full bg-white">
      {/* Hero — masthead style */}
      <div className="relative">
        <div className="relative h-72 sm:h-80 bg-[#0B2421] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1200')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B2421]/40 to-[#0B2421]/80" />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative h-full flex flex-col items-center justify-center text-center px-4 pb-16"
          >
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-2">Ferry Booking</p>
            <h1 className="text-white font-bold text-3xl sm:text-4xl mb-1">Book Your Ferry</h1>
            <p className="text-white/60 text-sm">Singapore & Batam routes</p>
          </motion.div>
        </div>

        {/* Search form overlapping the banner */}
        <div className="absolute bottom-0 left-0 right-0 z-30 translate-y-1/2 px-3 sm:px-4">
          <div className="max-w-4xl mx-auto rounded-2xl shadow-2xl border border-white/50 p-3 bg-white/60 backdrop-blur-2xl">
            {/* Row 1: From | To (with swap) | Trip Type */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              {/* From */}
              <Select value={originPort} onValueChange={setOriginPort}>
                <SelectTrigger className="h-12 bg-white/70 border-white/60 rounded-xl text-gray-700 text-sm shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="From (port)" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black [&_[data-highlighted]]:bg-gray-100">
                  {ports.map((port) => <SelectItem key={port} value={port}>{port}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* To + swap */}
              <div className="flex gap-1.5">
                <Select value={destinationPort} onValueChange={setDestinationPort}>
                  <SelectTrigger className="h-12 flex-1 min-w-0 bg-white/70 border-white/60 rounded-xl text-gray-700 text-sm shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0">
                    <SelectValue placeholder="To (port)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-black [&_[data-highlighted]]:bg-gray-100">
                    {ports.map((port) => <SelectItem key={port} value={port}>{port}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={swapPorts}
                  className="h-12 w-10 bg-white/70 border-white/60 hover:bg-[#0B2421] hover:text-white hover:border-[#0B2421] rounded-xl shrink-0">
                  <ArrowLeftRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Trip Type */}
              <Select value={tripType} onValueChange={setTripType}>
                <SelectTrigger className="h-12 bg-white/70 border-white/60 rounded-xl text-gray-700 text-sm shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Trip type" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black [&_[data-highlighted]]:bg-gray-100">
                  <SelectItem value="all">All Trips</SelectItem>
                  <SelectItem value="one-way">One-Way</SelectItem>
                  <SelectItem value="round-trip">Round-Trip</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: Date | Return Date | Passengers | Search */}
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3">
              {/* Departure Date */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="w-full flex items-center gap-2 bg-white/70 rounded-xl px-4 py-3 border border-white/60 text-left">
                    <CalendarIcon className="h-4 w-4 text-[#0B2421] shrink-0" />
                    <span className={`text-sm flex-1 truncate ${selectedDate ? "text-gray-700" : "text-gray-400"}`}>
                      {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Departure date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContentComp className="w-auto p-0" align="start">
                  <Calendar classNames="" className={{}} mode="single" selected={selectedDate} onSelect={setSelectedDate}
                    disabled={/** @param {Date} date */ (date) => date < new Date()} />
                </PopoverContentComp>
              </Popover>

              {/* Return Date */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className={`w-full flex items-center gap-2 rounded-xl px-4 py-3 border text-left ${tripType === "round-trip" ? "bg-white/70 border-white/60" : "bg-white/30 border-white/30 opacity-50 cursor-not-allowed"}`}
                    disabled={tripType !== "round-trip"}>
                    <CalendarIcon className="h-4 w-4 text-[#0B2421] shrink-0" />
                    <span className={`text-sm flex-1 truncate ${returnDate ? "text-gray-700" : "text-gray-400"}`}>
                      {returnDate ? format(returnDate, "dd MMM yyyy") : "Return date"}
                    </span>
                  </button>
                </PopoverTrigger>
                {tripType === "round-trip" && (
                  <PopoverContentComp className="w-auto p-0" align="start">
                    <Calendar classNames="" className={{}} mode="single" selected={returnDate} onSelect={setReturnDate}
                      disabled={/** @param {Date} date */ (date) => !selectedDate || date < selectedDate || date < new Date()} />
                  </PopoverContentComp>
                )}
              </Popover>

              {/* Passengers */}
              <div className="h-12 flex items-center gap-2 bg-white/70 rounded-xl px-4 border border-white/60">
                <Users className="h-4 w-4 text-[#0B2421] shrink-0" />
                <input
                  type="number"
                  min={1}
                  max={8}
                  value={passengerCount}
                  onChange={(e) => setPassengerCount(Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 1)))}
                  className="text-sm flex-1 bg-transparent outline-none text-gray-700 placeholder:text-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="flex flex-col shrink-0">
                  <button
                    type="button"
                    aria-label="Increase passengers"
                    onClick={() => setPassengerCount((n) => Math.min(8, n + 1))}
                    className="text-gray-400 hover:text-[#0B2421] leading-none transition-colors"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Decrease passengers"
                    onClick={() => setPassengerCount((n) => Math.max(1, n - 1))}
                    className="text-gray-400 hover:text-[#0B2421] leading-none transition-colors"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Search */}
              <button className="flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold rounded-xl px-8 py-3 transition-colors whitespace-nowrap">
                <Search className="h-4 w-4" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for overlapping card */}
      <div className="pt-52 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#0B2421]">
                {filteredFerries.length} {filteredFerries.length === 1 ? "Ferry" : "Ferries"} Available
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                {originPort && destinationPort
                  ? `${originPort} → ${destinationPort}`
                  : originPort || destinationPort
                    ? "Showing filtered results"
                    : "Showing all available ferries"}
              </p>
            </div>
            {(originPort || destinationPort) && (
              <Button onClick={showAllFerries} variant="outline" className="gap-2 border-gray-200 text-[#0B2421] rounded-xl">
                Show All Ferries
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">Loading ferries...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFerries.map((ferry, index) => (
                <FerryCard key={ferry.id} ferry={ferry}
                  onSelect={() => onSelectFerry(ferry, selectedDate, returnDate, passengerCount, tripType === "round-trip")}
                  index={index} selectedDate={selectedDate} returnDate={returnDate}
                  passengerCount={passengerCount} isRoundTrip={tripType === "round-trip"} />
              ))}
            </div>
          )}

          {!isLoading && filteredFerries.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-[#0B2421]/8 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-[#0B2421]/40" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B2421] mb-2">No ferries found</h3>
              <p className="text-gray-400">Try selecting different ports</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
